/**
 * Chance em foco no topo da apuração (§7.14 R1 nova; decisão do responsável de 18/10 ~23h10, ORD-F-099 item 1;
 * docs/DECISOES.md §71). Integrador do `ProbabilitySlot` variante `focus`, que mora no chunk `probability` de sempre:
 * o único `import()` dele está aqui (antes em panelsSlot.tsx), sem pedaço novo (DECISOES §63 e §67).
 *
 * Onde ela aparece (`chanceFocusPlace`), só na aba Presidente e com `features.probability`:
 * - computador largo (palco ≥ 1280; ORD-F-112): monta nas duas regiões e o CSS mostra uma só, pela altura lógica do
 *   palco (media query de altura e proporção em ProbabilitySlot.css): palco alto → topo da coluna direita, acima de
 *   "Por região"; palco < 1100 lógicos (todo laptop e toda tela 16:9, 16:10 e 3:2) → dentro do cartão principal, e a curva sai da coluna
 *   esquerda. A escondida tem `display: none` (fora da árvore de acessibilidade); ids por `useId`; um só pedido do chunk;
 * - computador estreito (menos `tiny`), tablet e celular em pé: dentro do cartão principal, logo depois da diferença;
 * - TV, celular deitado e computador estreito `tiny`: não cabe acima da dobra; fica a faixa compacta de hoje
 *   (`ProbabilityStrip`, C01). A faixa também segura o lugar enquanto o chunk não chega.
 * Some, como antes, com a chave desligada, antes das 17h00, na definição matemática e no Eleito · TSE. Ajustes da
 * ORD-F-104: some também com lugar ou perfil em foco (volta ao fechar) e em espera (fica como hoje: nada, porque a faixa
 * compacta só aparece com a chance ativa); em pausa fica no lugar, sem número.
 *
 * Arranjos da ORD-F-151 (trava `CHANCE_LAYOUT`, app/chanceLayout.ts; padrão 'today' = o de cima): A, o palco (cartão
 * próprio: topo da coluna direita no computador largo, região `top` acima do herói nos outros modos); B, o herói
 * dividido (sempre dentro do cartão principal, logo depois do apurado). Nos dois, a espera aparece depois das 17h00, e
 * o lugar fica reservado (`chance-reserve`, chanceFocusSlot.css) enquanto o pedaço `probability` não chega: nada pula.
 */
import './chanceFocusSlot.css';
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { ProbabilityStrip, presidentStrip } from '../features/probability/ProbabilityStrip';
import {
  type ChanceFocusPlace,
  type ChanceFocusRegion,
  chanceFocusPlace,
  chanceFocusShows,
  chanceFocusState,
  stripActive,
} from '../features/probability/slotState';
import { useConfig, useHasFocus, useLayout, useRouteState, useTimeline, useView } from '../state/hooks';
import { CHANCE_LAYOUT, type ChanceLayout } from './chanceLayout';
import { isWide } from './layoutMode';
import { lazyChunk, useLazyChunk } from './lazyChunk';

/** Chunk `probability` (§7.14, DE-C20): só pedido com a chance em foco por mostrar. */
export const probabilityChunk = lazyChunk(() => import('../features/probability'));

/**
 * Onde a chance em foco está agora: o lugar pelo modo de tela, ou null quando ela não aparece (outra aba, chave
 * desligada, antes das 17h00, presidência definida, status off/error/ended, ou tela em que não cabe).
 */
export function useChanceFocusPlace(arrangement: ChanceLayout = CHANCE_LAYOUT): ChanceFocusPlace | null {
  const route = useRouteState();
  const cfg = useConfig();
  const view = useView();
  const layout = useLayout();
  const { displayTMin } = useTimeline();
  const hasFocus = useHasFocus();
  if (route.office !== 'president') return null;
  // ORD-F-104 item 5: em espera o bloco não ocupa o topo (fica como hoje: a faixa compacta, que só aparece ativa); nos
  // arranjos A e B (ORD-F-151) a espera aparece depois das 17h00
  if (!chanceFocusShows(chanceFocusState(cfg, view, displayTMin), displayTMin, arrangement)) return null;
  // ORD-F-104 item 4: com lugar ou perfil em foco o bloco some (volta ao fechar); só o computador mostra o herói com foco
  if (hasFocus) return null;
  return chanceFocusPlace(layout, isWide(layout), arrangement);
}

/**
 * A chance em foco na região pedida, dentro da própria boundary (`chance`, quieta): uma falha do chunk ou de render
 * nunca derruba o herói nem a coluna direita. No cartão principal, enquanto a variante `focus` não está ali, a faixa
 * compacta (só com a chance ativa, pelas regras do slot).
 */
export function ChanceFocusSlot({
  region,
  arrangement = CHANCE_LAYOUT,
}: {
  region: ChanceFocusRegion;
  arrangement?: ChanceLayout;
}) {
  return (
    <ErrorBoundary name="chance" quiet>
      <ChanceFocusInner region={region} arrangement={arrangement} />
    </ErrorBoundary>
  );
}

function ChanceFocusInner({ region, arrangement }: { region: ChanceFocusRegion; arrangement: ChanceLayout }) {
  const place = useChanceFocusPlace(arrangement);
  const mod = useLazyChunk(probabilityChunk, place !== null);
  const cfg = useConfig();
  const view = useView();
  const hasFocus = useHasFocus();
  const { mode } = useLayout();
  const { displayTMin } = useTimeline();
  if (mod && (place === region || place === 'split'))
    return (
      <mod.ProbabilitySlot variant="focus" nested={region === 'hero'} split={place === 'split'} stage={arrangement} />
    );
  // A e B (ORD-F-151): enquanto o pedaço não chega, o lugar reservado, com a mesma altura mínima do bloco; sem a faixa
  // compacta (ela tem outra altura, e a troca empurraria o resto). Onde a chance em foco não cabe (TV, celular deitado,
  // computador estreito `tiny`), a faixa compacta de hoje, como no 'today'
  if (arrangement !== 'today' && place !== null) {
    if (place !== region) return null;
    return <div class={`chance-reserve chance-reserve--${arrangement}`} data-region={region} aria-hidden="true" />;
  }
  // com lugar em foco (herói na coluna esquerda do computador), nem o bloco nem a faixa: a faixa compacta não leva o
  // rótulo fixo (R6), e a ORD-F-104 item 4 só a quer com ele, sem texto novo
  if (region !== 'hero' || hasFocus || (mod && place !== null)) return null;
  const p = view ? stripActive(cfg, view, mode, displayTMin) : null;
  return p && view ? <ProbabilityStrip {...presidentStrip(p)} candidates={view.candidates} /> : null;
}
